import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { SELLING_TIPS, SELLING_TIP_ORDER } from "@/data/sellingTipsContent";

const SITE = "https://lagooncityrealestate.ca";

export default function SellingTipDetail() {
  const { slug } = useParams();
  const tip = SELLING_TIPS[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!tip) return <Navigate to="/selling-tips" replace />;
  const idx = SELLING_TIP_ORDER.indexOf(slug);
  const next = SELLING_TIP_ORDER[(idx + 1) % SELLING_TIP_ORDER.length];
  const canonical = `${SITE}/selling-tips/${slug}`;

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{`${tip.title} — Selling Tips | Joselle Alves`}</title>
        <meta name="description" content={tip.intro} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${tip.title} — Selling Tips | Joselle Alves`} />
        <meta property="og:description" content={tip.intro} />
        <meta property="og:url" content={canonical} />
      </Helmet>
      <PageHeader />
      <section className="relative border-b border-[#E890A7]/10">
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-12 sm:pt-32 sm:pb-16">
          <Link to="/selling-tips" data-testid="selling-tip-back-link" className="inline-flex items-center gap-2 text-sm text-[#F2A7BA] hover:gap-3 transition-all duration-300 mb-8">
            <ArrowLeft size={16} /> All Selling Tips
          </Link>
          <div className="flex items-start gap-6">
            <img src={tip.img} alt={tip.title} className="hidden sm:block w-28 h-28 object-contain shrink-0" />
            <div>
              <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-3">Selling Tips</p>
              <h1 data-testid="selling-tip-title" className="font-display font-light text-4xl sm:text-5xl leading-tight">{tip.title}</h1>
              <p className="mt-5 text-slate-300 font-light text-lg">{tip.intro}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
        <div className="space-y-12">
          {tip.sections.map((s, i) => (
            <motion.div key={s.h} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
              <h2 className="font-display text-2xl sm:text-3xl text-[#F2A7BA] leading-tight mb-5">{s.h}</h2>
              {s.blocks && s.blocks.map((b, j) => {
                if (b.p) return <p key={j} className="text-slate-300/90 font-light leading-relaxed mb-4">{b.p}</p>;
                if (b.ul) return (
                  <ul key={j} className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-4">
                    {b.ul.map((li) => (
                      <li key={li} className="flex items-start gap-3 text-sm text-slate-300 font-light">
                        <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-[#E890A7]/12 border border-[#E890A7]/35 flex items-center justify-center"><Check size={11} className="text-[#E890A7]" /></span>
                        {li}
                      </li>
                    ))}
                  </ul>
                );
                return null;
              })}
              {s.links && (
                <ul className="grid sm:grid-cols-2 gap-3">
                  {s.links.map((l) => (
                    <li key={l.href}>
                      <a href={l.href} target="_blank" rel="noreferrer" className="group flex items-center justify-between gap-3 rounded-xl border border-[#E890A7]/15 bg-[#151218]/60 px-4 py-3 text-sm text-slate-300 hover:text-[#F2A7BA] hover:border-[#E890A7]/45 transition-colors">
                        {l.label}
                        <ArrowUpRight size={15} className="shrink-0 text-[#E890A7]/70 group-hover:text-[#F2A7BA] transition-colors" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
        <div className="mt-16 rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-10">
          <h2 className="font-display font-light text-2xl sm:text-3xl leading-tight">Ready to sell in Lagoon City?</h2>
          <p className="mt-3 text-slate-400 font-light">Get a free home evaluation or ask Joselle anything — no obligation.</p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link data-testid="selling-tip-cta-eval" to="/home-evaluation" className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300">
              Free home evaluation <ArrowRight size={16} />
            </Link>
            <Link data-testid="selling-tip-next-link" to={`/selling-tips/${next}`} className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 transition-all duration-300">
              Next: {SELLING_TIPS[next].title} <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
